import { cloneElement, isValidElement, VNode } from 'preact';

import type { JSX } from 'preact/jsx-runtime';

/**
 * Reloads the current page.
 */
export const reloadWindow = () => window.location.reload();

/**
 * A helper function to check if a value is "empty".
 * It considers null, undefined, empty strings, empty arrays,
 * and empty objects as empty.
 *
 * @param {unknown} value The value to check.
 * @returns {boolean} True if the value is empty, otherwise false.
 */
export const isEmpty = (value: unknown): boolean => {
	// Handles null and undefined
	if (value == null) {
		return true;
	}

	if (Array.isArray(value) || typeof value === 'string') {
		return value.length === 0;
	}

	if (typeof value === 'object' && value !== null) {
		return Object.keys(value).length === 0;
	}

	return false;
};

export const clipboard = async (copyText: string): Promise<boolean> => {
	// First attempt: Modern Clipboard API
	if (
		navigator.clipboard &&
		typeof navigator.clipboard.writeText === 'function'
	) {
		try {
			await navigator.clipboard.writeText(copyText);
			return true;
		} catch (error) {
			console.warn('Clipboard API failed, using fallback:', error);
			// Clipboard API blocked by permissions policy - dynamically load fallback
			// Common in Instagram, Facebook in-app browsers, and some Chrome versions
			try {
				const { fallbackCopyTextToClipboard } =
					await import('./clipboard-fallback.ts');
				return fallbackCopyTextToClipboard(copyText);
			} catch (importError) {
				console.error('Failed to load clipboard fallback:', importError);
				return false;
			}
		}
	}

	// Second attempt: Fallback for older browsers or when Clipboard API is unavailable
	// Dynamically import fallback only when needed (code-splitting)
	try {
		const { fallbackCopyTextToClipboard } =
			await import('./clipboard-fallback.ts');
		return fallbackCopyTextToClipboard(copyText);
	} catch (importError) {
		console.error('Failed to load clipboard fallback:', importError);
		return false;
	}
};

export const formatAmount = (
	amount: number,
	options: { trimTrailingZeros?: boolean } = {},
): string => {
	if (amount === undefined) return '';
	const rupees = (amount / 100).toFixed(2);
	const CURRENCY_SYMBOL = '₹';
	return `${CURRENCY_SYMBOL}${Number(rupees).toLocaleString('en-IN', {
		minimumFractionDigits: options.trimTrailingZeros ? 0 : 2,
		maximumFractionDigits: 2,
	})}`;
};

export const formatDate = (timestamp: number): string => {
	const date = new Date(timestamp);

	const datePart = new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		year: '2-digit',
	}).format(date);

	const timePart = new Intl.DateTimeFormat('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	}).format(date);
	const formattedDate = `${datePart} | ${timePart}`;
	return formattedDate;
};

export const safeExecute = <T>(
	callback: () => T,
	fallback?: T,
	onError?: (error: unknown) => void,
): T => {
	try {
		return callback();
	} catch (error) {
		onError?.(error);
		return fallback as T;
	}
};

/**
 * Replaces text placeholders in a string with JSX or the result of a callback function.
 * * @param {string} text - The base string containing placeholders.
 * @param {Object} replacementMap - A map of {"placeholder": JSX.Element | (params) => JSX.Element}
 * @param {Object} [paramsMap] - Optional map providing params to specific placeholders: {"placeholder": [arg1, arg2]} - use this if your replacementMap is defined with functions that require arguments.
 * @returns {React.ReactNode[]} - An array of strings and JSX elements ready to render.
 */
export function replaceTextWithJSX(
	text: string,
	replacementMap: Record<
		string,
		JSX.Element | ((...args: unknown[]) => JSX.Element)
	>,
	paramsMap: Record<string, unknown[]> = {},
) {
	if (!text) return [];
	if (!replacementMap || Object.keys(replacementMap).length === 0)
		return [text];

	// 1. Filter out empty keys to prevent broken regex, then escape special characters and join them with '|'
	const keys = Object.keys(replacementMap)
		.filter(Boolean)
		.map((key) => key.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'));

	// If map only contained empty keys, return the original text
	if (keys.length === 0) return [text];

	const regex = new RegExp(`(${keys.join('|')})`, 'g');

	// 2. Split and map through the parts
	return text.split(regex).map((part, index) => {
		if (Object.prototype.hasOwnProperty.call(replacementMap, part)) {
			const valueOrValueFactory = replacementMap[part];
			let element;

			// 3. If it's a function, execute it with the latest params (fallback to empty array if none provided)
			if (typeof valueOrValueFactory === 'function') {
				const params = paramsMap[part] || [];
				// Ensure params are treated as an array to spread them cleanly
				element = valueOrValueFactory(
					...(Array.isArray(params) ? params : [params]),
				);
			} else {
				element = valueOrValueFactory;
			}

			// 4. Safely inject a unique key so re-renders work flawlessly
			if (isValidElement(element)) {
				return cloneElement(element as VNode, {
					key: `replaced-${part}-${index}`,
				});
			}

			return element;
		}

		return part;
	});
}
